/**
 * Sneakers Feel — aligne les packshots sur une même ligne de semelle
 * et harmonise l’échelle (largeur + hauteur du contenu).
 *
 * Stabilité :
 * - analyse via Image CORS dédiée (les <img> du DOM sans crossOrigin
 *   taintent le canvas → échecs aléatoires selon le cache)
 * - ResizeObserver + retries si le cadre n’a pas encore de dimensions
 *   (carrousel / layout différé)
 */
(function () {
  const SELECTOR =
    '.product-card .image-component.product-media--primary-image img, ' +
    '.product-card .product-media--primary-image img, ' +
    '.banner-grid .banner__media picture img, ' +
    '.banner-grid .banner__media .banner__media-image img, ' +
    '.template-list-collections .collection-card__image .image-component img, ' +
    '.sf-gender-native .sf-pastille__img';
  const SAMPLE = 200;
  const TARGET_BOTTOM = 0.88;
  const TARGET_CONTENT_W = 0.78;
  const TARGET_CONTENT_H = 0.48;
  /* Pastilles (sans cercle) : semelles alignées, légèrement remontées */
  const PASTILLE_TARGET_BOTTOM = 0.82;
  const PASTILLE_TARGET_CONTENT_W = 0.86;
  const PASTILLE_TARGET_CONTENT_H = 0.52;
  const MAX_LAYOUT_RETRIES = 12;

  /** @type {Map<string, Promise<object|null>>} */
  const boundsCache = new Map();
  /** @type {WeakSet<Element>} */
  const observedMedia = new WeakSet();
  /** @type {WeakMap<HTMLImageElement, number>} */
  const layoutRetries = new WeakMap();

  function isBackground(r, g, b, a) {
    if (a < 20) return true;
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    if (lum >= 235) return true;
    if (lum >= 210 && chroma < 28) return true;
    return false;
  }

  /* Ombre de contact sous la semelle (gris très pâle) — ignorée pour le bas */
  function isSoftGroundShadow(r, g, b, a) {
    if (isBackground(r, g, b, a)) return true;
    const lum = 0.299 * r + 0.587 * g + 0.114 * b;
    const chroma = Math.max(r, g, b) - Math.min(r, g, b);
    return lum >= 198 && chroma < 24;
  }

  function shouldSkip(img) {
    return Boolean(
      img.closest('.sf-coloris') ||
        img.classList.contains('sf-coloris__img') ||
        img.closest('.product-media--secondary-image')
    );
  }

  function imageKey(img) {
    return img.currentSrc || img.src || '';
  }

  function computeBoundsFromImage(im) {
    const w = im.naturalWidth;
    const h = im.naturalHeight;
    if (!w || !h) return null;

    const canvas = document.createElement('canvas');
    const tw = Math.min(SAMPLE, w);
    const th = Math.max(1, Math.round((h / w) * tw));
    canvas.width = tw;
    canvas.height = th;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;

    let data;
    try {
      ctx.drawImage(im, 0, 0, tw, th);
      data = ctx.getImageData(0, 0, tw, th).data;
    } catch (e) {
      return null;
    }

    const minRowHits = Math.max(2, Math.floor(tw * 0.02));
    const minColHits = Math.max(2, Math.floor(th * 0.02));
    let top = -1;
    let bottom = -1;
    let left = -1;
    let right = -1;

    for (let y = 0; y < th; y++) {
      let hits = 0;
      for (let x = 0; x < tw; x++) {
        const i = (y * tw + x) * 4;
        if (!isBackground(data[i], data[i + 1], data[i + 2], data[i + 3])) hits++;
      }
      if (hits >= minRowHits) {
        top = y;
        break;
      }
    }
    for (let y = th - 1; y >= 0; y--) {
      let hits = 0;
      for (let x = 0; x < tw; x++) {
        const i = (y * tw + x) * 4;
        /* Bas = vraie semelle, pas l’ombre douce au sol */
        if (!isSoftGroundShadow(data[i], data[i + 1], data[i + 2], data[i + 3])) hits++;
      }
      if (hits >= minRowHits) {
        bottom = y;
        break;
      }
    }
    for (let x = 0; x < tw; x++) {
      let hits = 0;
      for (let y = 0; y < th; y++) {
        const i = (y * tw + x) * 4;
        if (!isBackground(data[i], data[i + 1], data[i + 2], data[i + 3])) hits++;
      }
      if (hits >= minColHits) {
        left = x;
        break;
      }
    }
    for (let x = tw - 1; x >= 0; x--) {
      let hits = 0;
      for (let y = 0; y < th; y++) {
        const i = (y * tw + x) * 4;
        if (!isBackground(data[i], data[i + 1], data[i + 2], data[i + 3])) hits++;
      }
      if (hits >= minColHits) {
        right = x;
        break;
      }
    }

    if (top < 0 || bottom <= top || left < 0 || right <= left) return null;

    return {
      top: top / th,
      bottom: (bottom + 1) / th,
      left: left / tw,
      right: (right + 1) / tw
    };
  }

  function loadCorsImage(src) {
    return new Promise((resolve, reject) => {
      const im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.onload = () => resolve(im);
      im.onerror = () => reject(new Error('cors-image-load'));
      im.src = src;
    });
  }

  function loadBlobImage(src) {
    return fetch(src, { mode: 'cors', credentials: 'omit' })
      .then((res) => {
        if (!res.ok) throw new Error('fetch-failed');
        return res.blob();
      })
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const objUrl = URL.createObjectURL(blob);
            const im = new Image();
            im.onload = () => {
              URL.revokeObjectURL(objUrl);
              resolve(im);
            };
            im.onerror = () => {
              URL.revokeObjectURL(objUrl);
              reject(new Error('blob-image-load'));
            };
            im.src = objUrl;
          })
      );
  }

  function contentBounds(src) {
    if (!src) return Promise.resolve(null);
    if (boundsCache.has(src)) return boundsCache.get(src);

    const pending = loadCorsImage(src)
      .catch(() => loadBlobImage(src))
      .then((im) => computeBoundsFromImage(im))
      .catch(() => null);

    boundsCache.set(src, pending);
    return pending;
  }

  function reveal(media, img, mode) {
    img.dataset.sfPackshotAligned = mode;
    /* Forcer le reflow avec les styles d’alignement avant de rendre visible */
    void img.offsetWidth;
    media.classList.remove('sf-packshot-pending');
    media.classList.add('sf-packshot-ready');
  }

  function applyFallback(img, media) {
    img.style.removeProperty('position');
    img.style.removeProperty('inset');
    img.style.removeProperty('left');
    img.style.removeProperty('top');
    img.style.removeProperty('width');
    img.style.removeProperty('height');
    img.style.removeProperty('max-width');
    img.style.removeProperty('max-height');
    img.style.setProperty('object-fit', 'contain', 'important');
    const isPastille = media.classList.contains('sf-pastille__inner');
    img.style.setProperty(
      'object-position',
      isPastille ? 'center 62%' : 'center 88%',
      'important'
    );
    img.style.setProperty('transform', 'none', 'important');
    img.style.setProperty('margin', '0', 'important');
    img.style.removeProperty('clip-path');
    reveal(media, img, 'fallback');
  }

  function applyAlign(img, media, bounds) {
    const boxW = media.clientWidth;
    const boxH = media.clientHeight;
    if (!boxW || !boxH) return false;

    const natW = img.naturalWidth;
    const natH = img.naturalHeight;
    if (!natW || !natH) return false;

    const isPastille = media.classList.contains('sf-pastille__inner');
    const targetBottom = isPastille ? PASTILLE_TARGET_BOTTOM : TARGET_BOTTOM;
    const targetContentW = isPastille ? PASTILLE_TARGET_CONTENT_W : TARGET_CONTENT_W;
    const targetContentH = isPastille ? PASTILLE_TARGET_CONTENT_H : TARGET_CONTENT_H;

    const contentNatW = Math.max(1, (bounds.right - bounds.left) * natW);
    const contentNatH = Math.max(1, (bounds.bottom - bounds.top) * natH);
    const scaleW = (targetContentW * boxW) / contentNatW;
    const scaleH = (targetContentH * boxH) / contentNatH;
    const pxPerNat = Math.min(scaleW, scaleH);

    const drawW = natW * pxPerNat;
    const drawH = natH * pxPerNat;

    const baseline = boxH * targetBottom;
    const top = baseline - bounds.bottom * drawH;
    const contentCenterX = ((bounds.left + bounds.right) / 2) * drawW;
    const left = boxW / 2 - contentCenterX;

    img.style.setProperty('position', 'absolute', 'important');
    img.style.setProperty('inset', 'auto', 'important');
    img.style.setProperty('left', left + 'px', 'important');
    img.style.setProperty('top', top + 'px', 'important');
    img.style.setProperty('width', drawW + 'px', 'important');
    img.style.setProperty('height', drawH + 'px', 'important');
    img.style.setProperty('max-width', 'none', 'important');
    img.style.setProperty('max-height', 'none', 'important');
    img.style.setProperty('object-fit', 'fill', 'important');
    img.style.setProperty('object-position', 'center center', 'important');
    img.style.setProperty('transform', 'none', 'important');
    img.style.setProperty('margin', '0', 'important');
    img.style.removeProperty('clip-path');
    reveal(media, img, '1');
    layoutRetries.delete(img);
    return true;
  }

  function mediaFor(img) {
    return (
      img.closest('.sf-pastille__inner') ||
      img.closest('.image-component') ||
      img.closest('.banner__media') ||
      img.parentElement
    );
  }

  function scheduleLayoutRetry(img) {
    const n = layoutRetries.get(img) || 0;
    if (n >= MAX_LAYOUT_RETRIES) return;
    layoutRetries.set(img, n + 1);
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => alignImage(img));
    });
  }

  function observeMedia(media) {
    if (!media || observedMedia.has(media) || typeof ResizeObserver === 'undefined') return;
    observedMedia.add(media);
    resizeObserver.observe(media);
  }

  async function alignImage(img) {
    if (!img || shouldSkip(img)) return;
    if (!img.complete || !img.naturalWidth) return;

    const media = mediaFor(img);
    if (!media) return;
    observeMedia(media);

    const boxW = media.clientWidth;
    const boxH = media.clientHeight;
    if (!boxW || !boxH) {
      scheduleLayoutRetry(img);
      return;
    }

    try {
      if (img.decode) await img.decode();
    } catch (e) {
      /* decode optional */
    }

    const key = imageKey(img);
    const bounds = await contentBounds(key);
    if (!bounds) {
      applyFallback(img, media);
      return;
    }

    // srcset / re-layout peut avoir changé pendant l’analyse async
    if (imageKey(img) !== key) {
      alignImage(img);
      return;
    }

    if (!applyAlign(img, media, bounds)) {
      scheduleLayoutRetry(img);
    }
  }

  function prepare(img) {
    if (!img || shouldSkip(img) || img.dataset.sfPackshotBound === '1') return;
    img.dataset.sfPackshotBound = '1';

    const media = mediaFor(img);
    /* Masquer tant que l’alignement n’est pas prêt (pas de jump visible) */
    if (media && !media.classList.contains('sf-packshot-ready')) {
      media.classList.add('sf-packshot-pending');
      window.setTimeout(() => {
        if (media.classList.contains('sf-packshot-pending')) {
          applyFallback(img, media);
        }
      }, 1800);
    }

    const run = () => {
      /* Ne pas re-masquer un packshot déjà révélé (resize / srcset) */
      if (!media?.classList.contains('sf-packshot-ready')) {
        img.dataset.sfPackshotAligned = '';
      }
      alignImage(img);
    };
    // load se re-déclenche aussi quand le srcset change de densité
    img.addEventListener('load', run);
    if (img.complete && img.naturalWidth) run();
  }

  function scan(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(prepare);
  }

  function realignAll() {
    document.querySelectorAll(SELECTOR).forEach((img) => {
      if (shouldSkip(img)) return;
      img.dataset.sfPackshotAligned = '';
      layoutRetries.delete(img);
      alignImage(img);
    });
  }

  const resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const media = entry.target;
      const img = media.querySelector('img');
      if (!img || shouldSkip(img)) continue;
      alignImage(img);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }
  window.addEventListener('load', () => {
    scan();
    realignAll();
  });
  window.addEventListener('resize', () => {
    window.clearTimeout(window.__sfPackshotResize);
    window.__sfPackshotResize = window.setTimeout(realignAll, 120);
  });

  // Tabs / carrousels qui révèlent des slides déjà dans le DOM
  document.addEventListener(
    'click',
    () => {
      window.clearTimeout(window.__sfPackshotTab);
      window.__sfPackshotTab = window.setTimeout(realignAll, 80);
    },
    true
  );

  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (node.nodeType !== 1) continue;
        if (node.matches && node.matches('img') && node.closest('.product-card')) prepare(node);
        else if (node.querySelectorAll) scan(node);
      }
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
